import React from 'react';
import { View, Text, StyleSheet, Dimensions, Platform, TouchableOpacity } from 'react-native';
import Svg, { Path, Rect } from 'react-native-svg';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { createPortal } from 'react-dom';
import FontFamily from '../../../constants/fonts';
import { useSparcKpiFavorite } from '../hooks/useSparcKpiFavorite';
import { useBarHoverTooltip } from './BarHoverTooltip';

// Helper function to format numbers - removes trailing zeros after decimal
// e.g., 44.00 -> "44", 44.50 -> "44.5", 44.25 -> "44.25"
const formatNumber = (value: number | null, forceTwoDecimals = false): string => {
  if (value == null || !Number.isFinite(value)) return '--';
  const fixed = value.toFixed(2);
  return forceTwoDecimals ? fixed : parseFloat(fixed).toString();
};

interface ChartDataPoint {
  date: string;
  value: number | null;

}

interface SparcKpiCardProps {
  id: string;
  title: string;
  badgeLetter: string;
  badgeColor?: string;
  isMandatory?: boolean;
  actual: number | null;
  target: number | null;
  delta: number | null;
  invertDeltaColor?: boolean;
  chartData: ChartDataPoint[];
  isFavorite?: boolean;
  onFavoritePress?: () => void;
  onPress?: () => void;
  width?: number | string;
  height?: number;
  disableFavorite?: boolean;
  disableFavTooltip?: boolean;
  disableApiCall?: boolean; // Disable internal API call when parent handles it
}

const StarIcon = ({ filled = false, isLoading = false }: { filled?: boolean; isLoading?: boolean }) => {
  if (isLoading) {
    return (
      <MaterialIcons 
        name="autorenew" 
        size={14} 
        color="#616161"
        style={Platform.OS === 'web' ? ({
          animation: 'spin 1s linear infinite',
        } as any) : undefined}
      />
    );
  }
  
  return (
    <Svg width="14" height="13" viewBox="0 0 14 13" fill="none">
      {filled ? (
        <Path
          d="M8.54 4.41333L13.3333 4.82667L9.7 7.98L10.7867 12.6667L6.66667 10.18L2.54667 12.6667L3.64 7.98L0 4.82667L4.79333 4.42L6.66667 0L8.54 4.41333Z"
          fill="#3A3A3A"
        />
      ) : (
        <Path
          d="M8.54 4.41333L13.3333 4.82667L9.7 7.98L10.7867 12.6667L6.66667 10.18L2.54667 12.6667L3.64 7.98L0 4.82667L4.79333 4.42L6.66667 0L8.54 4.41333Z"
          fill="none"
          stroke="#616161"
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </Svg>
  );
};

const DownArrowIcon = ({ color }: { color: string }) => (
  <Svg width="5" height="5" viewBox="0 0 5 3" fill="none">
    <Path
      d="M0 0L2.5 2.5L5 0H0Z"
      fill={color}
    />
  </Svg>
);

const UpArrowIcon = ({ color }: { color: string }) => (
  <Svg width="5" height="5" viewBox="0 0 5 3" fill="none">
    <Path
      d="M5 2.5L2.5 0L0 2.5H5Z"
      fill={color}
    />
  </Svg>
);

const LockIcon = () => (
  <MaterialIcons name="lock" size={14} color="#616161" />
);

// Tooltip component for "Add to Stand-up" on favorite button - renders via portal
const FavoriteTooltip: React.FC<{
  visible: boolean;
  position: { x: number; y: number } | null;
}> = ({ visible, position }) => {
  if (Platform.OS !== 'web' || !visible || !position) {
    return null;
  }

  const bubbleWidth = 120;
  const bubbleHeight = 34;

  const left = position.x - bubbleWidth / 2;
  const top = position.y - bubbleHeight - 10;

  const overlay = (
    <View
      style={{
        position: 'fixed' as any,
        left,
        top,
        width: bubbleWidth,
        zIndex: 2147483647,
        alignItems: 'center',
        pointerEvents: 'none',
      }}
    >
      <View style={{
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        backgroundColor: '#1C1C1C',
      }}>
        <Text style={{
          color: '#FFFFFF',
          fontSize: 12,
          fontWeight: '400',
          textAlign: 'center',
        }}>Add to Stand-up</Text>
      </View>
      <View style={{
        width: 0,
        height: 0,
        borderLeftWidth: 6,
        borderRightWidth: 6,
        borderTopWidth: 6,
        borderLeftColor: 'transparent',
        borderRightColor: 'transparent',
        borderTopColor: '#1C1C1C',
        marginTop: -1,
      }} />
    </View>
  );

  return createPortal(overlay, document.body);
};

const SparcKpiCard: React.FC<SparcKpiCardProps> = ({
  id,
  title,
  badgeLetter,
  badgeColor = '#D4F4DD',
  isMandatory = false,
  actual,
  target,
  delta,
  chartData,
  isFavorite = false,
  onFavoritePress,
  onPress,
  width,
  height,
  disableFavorite = false,
  disableFavTooltip = false,
  disableApiCall = false,
  invertDeltaColor = false,
}) => {
  const [isStarFilled, setIsStarFilled] = React.useState(isFavorite);
  const { toggleFavorite, isLoading } = useSparcKpiFavorite();
  
  // Tooltip state for "Add to Stand-up" on favorite button hover
  const [isFavTooltipVisible, setIsFavTooltipVisible] = React.useState(false);
  const [favTooltipPosition, setFavTooltipPosition] = React.useState<{ x: number; y: number } | null>(null);
  const isWeb = Platform.OS === 'web';

  // Sync state with prop changes
  React.useEffect(() => {
    setIsStarFilled(isFavorite);
    if (isFavorite || isMandatory) {
      setIsFavTooltipVisible(false);
      setFavTooltipPosition(null);
    }
  }, [isFavorite, isMandatory]);

  const handleStarPress = async () => {
    if (isLoading || isMandatory) return;
    
    // When disableApiCall is true (Selected section), parent handles everything
    // No optimistic updates - let parent control state via isFavorite prop
    if (disableApiCall) {
      if (onFavoritePress) {
        onFavoritePress();
      }
      return;
    }
    
    // Only do optimistic UI update when we control the API call
    setIsStarFilled(!isStarFilled);
    if (!isStarFilled) {
      setIsFavTooltipVisible(false);
      setFavTooltipPosition(null);
    }
    
    if (onFavoritePress) {
      onFavoritePress();
    }

    // Make API call using the hook
    const success = await toggleFavorite(id, title);
    
    if (!success) {
      // Revert optimistic update on failure
      setIsStarFilled(!isStarFilled);
    } else {
      // Scroll to top on web after successful favorite action from card
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  // Handle mouse enter for favorite button tooltip (web only)
  const handleFavMouseEnter = (e: any) => {
    if (!isWeb || disableFavorite || disableFavTooltip || isMandatory) return;
    if (isStarFilled) return; // don't show tooltip if already favorited
    const rect = e?.currentTarget?.getBoundingClientRect?.();
    if (rect) {
      setFavTooltipPosition({ 
        x: rect.left + rect.width / 2, 
        y: rect.top 
      });
      setIsFavTooltipVisible(true);
    }
  };

  // Handle mouse leave for favorite button tooltip
  const handleFavMouseLeave = React.useCallback(() => {
    setIsFavTooltipVisible(false);
    setFavTooltipPosition(null);
  }, []);

  // Inject CSS animation for spinning icon on web
  React.useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const styleId = 'sparc-kpi-spinner-animation';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `;
        document.head.appendChild(style);
      }
    }
  }, []);

  // Cleanup tooltip on unmount
  React.useEffect(() => {
    return () => {
      setIsFavTooltipVisible(false);
      setFavTooltipPosition(null);
    };
  }, []);

  // Calculate chart dimensions and scaling based on card width
  const windowWidth = Dimensions.get('window').width;
  const cardWidth = typeof width === 'string' 
    ? (width.includes('%') ? windowWidth * (parseFloat(width) / 100) : parseFloat(width))
    : (width || (windowWidth > 768 ? 680 : windowWidth - 80));

  const { getBarProps, tooltip: barTooltip } = useBarHoverTooltip();

  const orderedChartData = React.useMemo(() => {
    if (!chartData || chartData.length === 0) return chartData;
    const parsed = chartData.map((d, i) => ({ ...d, _ts: Date.parse(d.date), _idx: i }));
    const allValid = parsed.every(p => !isNaN(p._ts));
    if (allValid) {
      return parsed.sort((a, b) => a._ts - b._ts).map(({ _ts, _idx, ...rest }) => rest);
    }
    return [...chartData].reverse();
  }, [chartData]);

  const dataMax = Math.max(...orderedChartData.map(d => d.value ?? 0), target ?? 0, actual ?? 0);
  const maxValue = dataMax > 0 ? dataMax * 1.1 : 1;
  const chartHeight = 72; // Match LighthouseKpiCard height
  const chartWidth = cardWidth - 80; // Account for padding and margins
  const barWidth = (chartWidth - 40) / orderedChartData.length;
  const barSpacing = Math.max(4, barWidth * 0.1);
  const actualBarWidth = Math.max(barWidth - barSpacing, 8);

  const getBarHeight = (value: number) => {
    return (value / maxValue) * chartHeight;
  };

  const isDeltaNegative = delta != null && delta < 0; // Negative delta (actual < target, bad - red, down arrow)
  const isDeltaPositive = delta != null && delta > 0; // Positive delta (actual > target, good - green, up arrow)
  const isDeltaBad = invertDeltaColor ? isDeltaPositive : isDeltaNegative;
  const isDeltaGood = invertDeltaColor ? isDeltaNegative : isDeltaPositive;
  const deltaArrowColor = isDeltaBad ? '#C12C01' : 'rgb(18, 159, 20)';

  const cardStyle: any = [
    styles.card,
    width ? { width } : null,
    height ? { height } : null,
  ].filter(Boolean);

  return (
    <>
      <FavoriteTooltip visible={isFavTooltipVisible} position={favTooltipPosition} />
      {barTooltip}
      <TouchableOpacity 
        style={cardStyle} 
        onPress={onPress}
        activeOpacity={onPress ? 0.7 : 1}
        disabled={!onPress}
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <View style={[styles.badge, { backgroundColor: badgeColor }]}>
            <Text style={styles.badgeText}>{badgeLetter}</Text>
          </View>
          <Text style={styles.title} numberOfLines={1} ellipsizeMode="tail">{title}</Text>
        </View>
        <TouchableOpacity 
          style={[
            styles.favoriteButton,
            isWeb && ({
              cursor: (disableFavorite || isMandatory) ? 'not-allowed' : (isLoading ? 'default' : 'pointer'),
            } as any),
          ]} 
          onPress={handleStarPress}
          disabled={isLoading || disableFavorite || isMandatory}
          {...(isWeb && !disableFavorite && !isMandatory
            ? ({
                onMouseEnter: handleFavMouseEnter,
                onMouseLeave: handleFavMouseLeave,
              } as any)
            : {})}
        >
          {isMandatory ? <LockIcon /> : <StarIcon filled={isStarFilled} isLoading={isLoading} />}
        </TouchableOpacity>
      </View>

      {/* Metrics Section */}
      <View style={styles.metricsContainer}>
        <View style={styles.metricItem}>
          <Text style={styles.metricLabel}>Actual</Text>
          <Text style={styles.metricValue}>{formatNumber(actual, id === 'gap_time')}</Text>
        </View>
        <View style={styles.metricItem}>
          <Text style={styles.metricLabel}>Target</Text>
          <Text style={styles.metricValue}>{formatNumber(target, id === 'gap_time')}</Text>
        </View>
        <View style={styles.metricItem}>
          <Text style={styles.metricLabel}>Delta</Text>
          <View style={styles.deltaContainer}>
            {isDeltaNegative && <DownArrowIcon color={deltaArrowColor} />}
            {isDeltaPositive && <UpArrowIcon color={deltaArrowColor} />}
            <Text style={[styles.metricValue, isDeltaBad && styles.deltaValueRed, isDeltaGood && styles.deltaValueGreen]}>
              {formatNumber(delta == null ? null : id === 'gap_time' ? delta : Math.abs(delta), id === 'gap_time')}
            </Text>
          </View>
        </View>
      </View>

      {/* Chart Section */}
      <View style={styles.chartContainer}>
        {/* Y-axis label */}
        <View style={styles.yAxisContainer}>
          <Text style={styles.yAxisLabel}>{formatNumber(maxValue)}</Text>
          <View style={styles.yAxisLine} />
          <Text style={styles.yAxisLabel}>0</Text>
        </View>

        {/* Bars and X-axis */}
        <View style={styles.chartContent}>
          <View style={styles.barsContainer}>
            {orderedChartData.map((dataPoint, index) => {
              const barHeight = dataPoint.value == null ? 0 : getBarHeight(dataPoint.value);
              return (
                <View key={index} style={styles.barWrapper} {...getBarProps(dataPoint)}>
                  <View style={styles.barContainer}>
                    <View
                      style={[
                        styles.bar,
                        {
                          height: barHeight,
                          backgroundColor: dataPoint.value == null ? 'transparent' : '#41B8E6',
                        },
                      ]}
                    />
                  </View>
                </View>
              );
            })}
          </View>

          {/* X-axis labels */}
          <View style={styles.xAxisContainer}>
            {orderedChartData.map((dataPoint, index) => (
              <View key={index} style={styles.xAxisLabelWrapper}>
                <Text style={styles.xAxisLabel}>{dataPoint.date}</Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    </TouchableOpacity>
    </>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E0E7FF',
    padding: 16,
    flex: 1,
    // Remove hardcoded min/max width to allow responsive sizing from parent
    ...Platform.select({
      ios: {
        shadowColor: 'rgba(99, 102, 241, 0.1)',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 1,
        shadowRadius: 8,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0px 4px 8px rgba(99, 102, 241, 0.1)',
      },
    }),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    overflow: 'hidden',
  },
  badge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1B5E20',
    fontFamily: FontFamily.regular,
  },
  title: {
    fontSize: 16,
    fontWeight: '500',
    color: '#2D2D2D',
    fontFamily: FontFamily.medium,
    flexShrink: 1,
    overflow: 'hidden',
  },
  favoriteButton: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    flexShrink: 0,
  },
  metricsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
   // backgroundColor: '#F8FAFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E3F2FD',
    paddingVertical: 12,
    paddingHorizontal: 2,
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  metricItem: {
    flex: 1,
    alignItems: 'center',
  },
  metricLabel: {
    fontSize: 12,
    fontWeight: '400',
    color: '#4B4B4B',
    marginBottom: 8,
    fontFamily: FontFamily.regular,
  },
  metricValue: {
    fontSize: 16,
    fontWeight: '700',
    color: '#000000',
    fontFamily: FontFamily.regular,
  },
  deltaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  deltaValueRed: {
    color: '#D32F2F',
  },
  deltaValueGreen: {
    color: 'rgb(18, 159, 20)',
  },
  chartContainer: {
    flexDirection: 'row',
    gap: 8,
    minHeight: 100,
    WebkitPrintColorAdjust: 'exact',
    printColorAdjust: 'exact',
  } as any,
  yAxisContainer: {
   // width: 30,
    justifyContent: 'space-between',
    alignItems: 'flex-end',
   // paddingRight: 8,
   marginBottom:22,
   marginTop:-6,
  },
  yAxisLabel: {
    fontSize: 12,
    fontWeight: '400',
    color: '#616161',
    fontFamily: FontFamily.regular,
  },
  yAxisLine: {
    // flex: 1,
    // width: 1,
    // backgroundColor: '#E0E0E0',
   // marginVertical: 4,
  },
  chartContent: {
    flex: 1,
  },
  barsContainer: {
    flexDirection: 'row',
    height: 72,
    alignItems: 'flex-end',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    gap: 4,
    WebkitPrintColorAdjust: 'exact',
    printColorAdjust: 'exact',
  } as any,
  barWrapper: {
    flex: 1,
    alignItems: 'center',
    minWidth: 20,
  },
  barContainer: {
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  bar: {
    width: '55%',
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    minHeight: 2,
    WebkitPrintColorAdjust: 'exact',
    printColorAdjust: 'exact',
  } as any,
  xAxisContainer: {
    flexDirection: 'row',
    marginTop: 8,
    gap: 4,
  },
  xAxisLabelWrapper: {
    flex: 1,
    alignItems: 'center',
    minWidth: 20,
  },
  xAxisLabel: {
    fontSize: 12,
    fontWeight: '400',
    color: '#616161',
    fontFamily: FontFamily.regular,
  },
});

export default SparcKpiCard;

